// Per-product overrides for photos whose alt text or filename
// doesn't match the actual color visible in the photo.
// Format: { "<product-handle>": { "<media-id>": "<actual color lowercased>" } }
const MEDIA_COLOR_OVERRIDES = {
  "augusta-bag": {
    "56650888642931": "black", // augusta-bag-8988829.png — alt "White" but visually Black
    "56667924038003": "black", // augusta-bag-6921472.webp — alt neutral, visually Black
  },
  "coral-bucket-bag": {
    "56667938521459": "sagefield", // coral-bucket-bag-7978334.webp
    "56554722296179": "sagefield", // coral-bucket-bag-2535525.jpg
    "56667946844531": "sagefield", // coral-bucket-bag-4904009.webp
    "54518857728371": "sagefield", // coral-bucket-bag-3851200.jpg
    "54518857761139": "sagefield", // coral-bucket-bag-1639740.jpg
    "56667960312179": "shiraz",    // coral-bucket-bag-7970814.webp
    "56667952513395": "shiraz",    // coral-bucket-bag-1909172.webp
    "54518846882163": "shiraz",    // coral-bucket-bag-2299907.jpg
  },
};

if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.selectors = {
          container: ".m-main-product--wrapper",
          slider: ".swiper-container",
          sliderPagination: ".swiper-pagination",
          sliderPrevEl: ".swiper-button-prev",
          sliderNextEl: ".swiper-button-next",
          navSlider: ".nav-swiper-container",
          medias: [".m-product-media--item"],
          mediaWrapper: ".m-product-media--wrapper",
          mediaZoomIns: [".m-product-media__zoom-in"],
          videos: [".media-video"],
          variantIdNode: '[name="id"]',
          featuredImage: ".m-product-media",
        };

        this.productSlideCommonConfigs = { loop: true };

        this.productSlideConfigs = {
          mobile: { autoHeight: true, loop: true },
          "quick-view": { autoHeight: true },
          "layout-5": {
            ...this.productSlideCommonConfigs,
            slidesPerView: 1,
            slidesPerGroup: 1,
            watchSlidesProgress: true,
            watchSlidesVisibility: true,
            breakpoints: { 768: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 10 } },
          },
          "layout-7": {
            ...this.productSlideCommonConfigs,
            slidesPerView: 3,
            speed: 500,
            centeredSlides: true,
          },
        };

        this.container = this.closest(this.selectors.container);
        this.domNodes = queryDomNodes(this.selectors, this);
        this.enableVideoAutoplay = this.dataset.enableVideoAutoplay === "true";
        this.enableImageZoom = this.dataset.enableImageZoom === "true";
        this.enableVariantGroupImages = this.dataset.enableVariantGroupImages === "true";
        this.onlyMedia = this.dataset.onlyMedia === "true";
        if (this.container) {
          this.view = this.container.dataset.view || "product-template";
        }
        this.section = this.closest(`[data-section-id="${this.sectionId}"]`);
        this.layout = this.dataset.layout;
        this.fetchProductData();
      }

      getProductSliderConfig(layout) {
        const conf = this.productSlideConfigs[layout] || this.productSlideConfigs["layout-4"];
        return { ...this.productSlideCommonConfigs, ...conf };
      }

      async fetchProductData() {
        try {
          const product = await this.getProductJson();
          if (product) {
            this.productData = product;
            this.setupData();
          }
        } catch (error) {
          console.error("Error fetching product:", error);
        }
      }

      setupData() {
        this.productHandle = this.dataset.productHandle;
        this.productUrl = this.dataset.productUrl;
        const { variants } = this.productData || {};

        if (!this.productData || !variants) return;

        const variantIdNode = this.container.querySelector(this.selectors.variantIdNode);
        if (!variantIdNode) return;

        let currentVariantId = Number(variantIdNode.value) || this.productData.selected_or_first_available_variant.id;
        const currentVariant = variants.find((v) => v.id === currentVariantId) || variants[0];
        this.productData.initialVariant = currentVariant;

        if (!this.productData.selected_variant && variantIdNode.dataset.selectedVariant) {
          const selectedVariantId = Number(variantIdNode.dataset.selectedVariant);
          this.productData.selected_variant = variants.find((v) => v.id === selectedVariantId);
        }

        // Filter slides BEFORE init so grid/slider doesn't render foreign-color photos
        this.filterSlidesForVariant(currentVariant);

        this.init();
      }

      filterSlidesForVariant(variant) {
        if (!variant || this.enableVariantGroupImages) return;
        if (this.view !== "product-template") return;

        const opts = (this.productData && this.productData.options) || [];
        const colorOption = opts.find((o) => /colou?r/i.test(o.name)) || opts[0];
        if (!colorOption) return;
        const colorIdx = (colorOption.position || 1) - 1;
        const variantOptions = variant.options || [];
        const selected = (variantOptions[colorIdx] || variant.option1 || variantOptions[0] || "").trim().toLowerCase();
        if (!selected) return;

        const allColors = (colorOption.values || []).map((v) => v.toLowerCase());
        const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const colorRegexes = {};
        allColors.forEach((c) => { colorRegexes[c] = new RegExp("\\b" + escape(c) + "\\b", "i"); });

        const overridesForProduct = MEDIA_COLOR_OVERRIDES[this.productHandle] || {};

        if (!this._slidePool) {
          this._slidePool = document.createElement("div");
          this._slidePool.hidden = true;
          this._slidePool.setAttribute("aria-hidden", "true");
          this._slidePool.dataset.variantSlidePool = "true";
          this._slidePool.style.display = "none";
          this.appendChild(this._slidePool);
        }

        let wrapper = this.querySelector(".swiper-wrapper");
        if (!wrapper) {
          const firstItem = this.querySelector(".m-product-media--item");
          wrapper = firstItem ? firstItem.parentElement : null;
        }
        if (!wrapper) return;
        this._slidesParent = wrapper;

        const all = [];
        wrapper.querySelectorAll(".m-product-media--item").forEach((s) => all.push(s));
        this._slidePool.querySelectorAll(".m-product-media--item").forEach((s) => all.push(s));

        const getMediaId = (s) => {
          const inner = s.querySelector(".m-product-media");
          return (
            (s.dataset && s.dataset.mediaId) ||
            (inner && (inner.dataset.mediaId || inner.getAttribute("data-media-id"))) ||
            ""
          );
        };
        const getAlt = (s) => {
          const img = s.querySelector("img");
          const inner = s.querySelector(".m-product-media");
          return (
            (img && img.alt) ||
            (inner && (inner.dataset.mediaAlt || inner.getAttribute("data-media-alt"))) ||
            ""
          ).toLowerCase();
        };
        const getFilename = (s) => {
          const img = s.querySelector("img");
          const inner = s.querySelector(".m-product-media");
          const candidate =
            (img && (img.currentSrc || img.src || img.dataset.src || img.getAttribute("src"))) ||
            (inner && (inner.dataset.mediaSrc || inner.getAttribute("data-media-src"))) ||
            "";
          try { return candidate.split("?")[0].split("/").pop().toLowerCase(); }
          catch (e) { return ""; }
        };

        const resolveColor = (s) => {
          const mid = String(getMediaId(s));
          if (overridesForProduct[mid]) return overridesForProduct[mid].toLowerCase();
          const alt = getAlt(s);
          for (const c of allColors) {
            if (colorRegexes[c].test(alt)) return c;
          }
          const filename = getFilename(s);
          for (const c of allColors) {
            if (colorRegexes[c].test(filename)) return c;
          }
          return "neutral";
        };

        const matching = [];
        const nonMatching = [];

        all.forEach((s) => {
          if (s.classList && s.classList.contains("swiper-slide-duplicate")) {
            nonMatching.push(s);
            return;
          }
          const photoColor = resolveColor(s);
          if (photoColor === selected || photoColor === "neutral") {
            matching.push(s);
          } else {
            nonMatching.push(s);
          }
        });

        if (matching.length === 0) {
          all.forEach((s) => {
            if (!(s.classList && s.classList.contains("swiper-slide-duplicate"))) matching.push(s);
          });
        }

        matching.forEach((s) => { if (s.parentNode !== wrapper) wrapper.appendChild(s); });
        nonMatching.forEach((s) => { if (s.parentNode !== this._slidePool) this._slidePool.appendChild(s); });

        this._lastFilterColor = selected;
      }

      init() {
        const variantPicker = this.container.querySelector("variant-picker");

        const commonInit = () => {
          this.handleAutoplayVideo();
          if (!variantPicker) {
            this.initPhotoswipe();
            this.handleSlideChange();
            this.removeAttribute("data-media-loading");
            this.firstElementChild.style.opacity = 1;
          }
        };

        switch (this.view) {
          case "product-template":
            this.mediaMode = "grid-column";
            this.handlePhotoswipe();
            this.initPhotoswipe();
            this.initSlider();
            break;
          case "featured-product":
            this.initSlider();
            break;
          case "card":
          case "sticky-atc":
          case "quick-view":
            this.mediaMode = "featured-image";
            if (this.view === "quick-view") {
              this.initSlider();
            }
            break;
          default:
            console.warn("Unknown product view: ", this, this.view);
        }

        commonInit();
      }

      initSlider() {
        if (!this.domNodes.slider) return;

        this.mediaMode = "slider";
        const { slider, sliderPagination, navSlider, sliderNextEl: nextEl, sliderPrevEl: prevEl } = this.domNodes;

        const initialSlide =
          (this.productData.initialVariant &&
            this.productData.initialVariant.featured_media &&
            this.productData.initialVariant.featured_media.position - 1) ||
          0;

        const configNav = this.getNavSliderConfig(initialSlide);
        this.navSlider = navSlider ? new MinimogLibs.Swiper(navSlider, configNav) : null;

        const config = this.getMainSliderConfig(initialSlide, { nextEl, prevEl, sliderPagination });
        this.slider = new MinimogLibs.Swiper(slider, config);

        if (!this.enableVariantGroupImages) this.handleSlideChange();
      }

      getNavSliderConfig(initialSlide) {
        return {
          loop: false,
          initialSlide,
          slidesPerView: 5,
          freeMode: true,
          spaceBetween: this.layout === "layout-6" ? 5 : 10,
          threshold: 2,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          breakpoints: {
            1024: { direction: this.layout === "layout-6" ? "vertical" : "horizontal" },
          },
          on: { init: () => (this.domNodes.navSlider.style.opacity = 1) },
        };
      }

      getMainSliderConfig(initialSlide, { nextEl, prevEl, sliderPagination }) {
        const thumbsConfig = this.navSlider ? { thumbs: { swiper: this.navSlider } } : {};
        return {
          ...this.getProductSliderConfig(this.layout),
          initialSlide,
          autoHeight: true,
          navigation: { nextEl, prevEl },
          threshold: 2,
          loop: !this.enableVariantGroupImages,
          pagination: { el: sliderPagination, clickable: true, type: "bullets" },
          ...thumbsConfig,
          on: {
            init: () => {
              this.domNodes.slider.style.opacity = 1;
              this.domNodes = queryDomNodes(this.selectors, this.container);
              requestAnimationFrame(() => { window.dispatchEvent(new Event("resize")); });
            },
          },
        };
      }

      handleSlideChange() {
        if (!this.slider) return;
        this.slider.on("slideChange", (swiper) => {
          window.pauseAllMedia(this);
          const { slides, activeIndex } = swiper;
          const activeSlide = slides[activeIndex];
          if (activeSlide) this.playActiveMedia(activeSlide);
          const visibleSlides = [activeIndex];
          if (["layout-5", "layout-7"].includes(this.layout)) visibleSlides.push(activeIndex + 1);
          const hasModelMediaType = visibleSlides.some((index) => {
            const slide = slides[index];
            return slide && slide.dataset.mediaType === "model";
          });
          this.toggleSliderDraggableState(!hasModelMediaType);
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.slider.allowTouchMove !== isDraggable) this.slider.allowTouchMove = isDraggable;
      }

      handleAutoplayVideo() {
        const playVideo = (mediaElement) => {
          const mediaType = mediaElement.dataset.mediaType;
          if (mediaType === "video" || mediaType === "external_video") {
            const deferredMedia = mediaElement.querySelector("deferred-media");
            const autoplay = deferredMedia && deferredMedia.dataset.autoPlay === "true";
            if (autoplay) deferredMedia.loadContent(false);
          }
        };

        if (this.mediaMode === "slider") {
          const { slides, activeIndex } = this.slider;
          const activeSlide = slides[activeIndex];
          if (activeSlide) playVideo(activeSlide);
        } else {
          const allMedia = this.querySelectorAll(".m-product-media--item");
          allMedia.forEach(playVideo);
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector("deferred-media");
        if (!deferredMedia || deferredMedia.dataset.autoPlay !== "true") return;

        const playMedia = (element) => {
          if (element.classList.contains("js-youtube") || element.classList.contains("js-vimeo")) {
            const platform = element.classList.contains("js-youtube") ? "youtube" : "vimeo";
            const param = platform === "youtube" ? "mute=1" : "muted=1";
            const symbol = element.src.includes("?") ? "&" : "?";
            element.src += `${symbol}autoplay=1&${param}`;
          } else {
            element.play();
          }
        };

        if (!deferredMedia.hasAttribute("loaded")) deferredMedia.loadContent(false);
        const deferredElement = deferredMedia.querySelector("video, model-viewer, iframe");
        playMedia(deferredElement);
      }

      handlePhotoswipe(customData = []) {
        if (!this.enableImageZoom) return;

        const medias = [...this.querySelectorAll(".m-product-media--item:not(.swiper-slide-duplicate)")];

        const data = medias.map((media) => {
          const { mediaType, index: id } = media.dataset;
          if (mediaType === "image") {
            const { mediaSrc: src, mediaWidth, mediaHeight, mediaAlt: alt } = media.querySelector(".m-product-media").dataset;
            return { src, width: parseInt(mediaWidth), height: parseInt(mediaHeight), alt, id };
          } else {
            return { html: `<div class="pswp__${mediaType}">${media.innerHTML}</div>`, type: mediaType, id };
          }
        });

        const options = {
          dataSource: customData.length ? customData : data,
          bgOpacity: 1, close: false, zoom: false, arrowNext: false, arrowPrev: false, counter: false, preloader: false,
          pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
        };
        this.lightbox = new MinimogLibs.PhotoSwipeLightbox(options);

        this.lightbox.addFilter("thumbEl", (thumbEl, { id }, index) => {
          return this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`) || thumbEl;
        });

        this.lightbox.addFilter("placeholderSrc", (placeholderSrc, { data: { id } }) => {
          const el = this.querySelector(`[data-index="${id}"]:not(.swiper-slide-duplicate) img`);
          return el ? el.src : placeholderSrc;
        });

        this.lightbox.on("change", () => {
          window.pauseAllMedia(this);
          if (this.slider) {
            const { currIndex } = this.lightbox.pswp;
            const slideMethod = this.enableVariantGroupImages ? "slideTo" : "slideToLoop";
            this.slider[slideMethod](currIndex, 100, false);
          }
        });

        this.lightbox.on("pointerDown", (e) => {
          if (this.lightbox.pswp.currSlide.data.type === "model") e.preventDefault();
        });

        const uiElements = [
          { name: "m-close", order: 11, isButton: true,
            html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
            onClick: (event, el, pswp) => pswp.close() },
          { name: "m-arrowNext", order: 12, isButton: true,
            html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
            onClick: (event, el, pswp) => pswp.next() },
          { name: "m-arrowPrev", order: 10, isButton: true,
            html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
            onClick: (event, el, pswp) => pswp.prev() },
        ];

        this.lightbox.on("uiRegister", () => {
          this.lightbox.pswp.ui.registerElement(uiElements[0]);
          if (!this.onlyMedia) {
            uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
          }
        });
      }

      initPhotoswipe() {
        if (!this.enableImageZoom) return;
        this.lightbox.init();

        const handleMediaClick = (e, media) => {
          e.preventDefault();
          const isImage = media.classList.contains("media-type-image");
          const isZoomButton = e.target.closest(this.selectors.mediaZoomIns[0]);
          if (isImage || isZoomButton) {
            const index = Number(media.dataset.index) || 0;
            this.lightbox.loadAndOpen(index);
          }
        };

        addEventDelegate({ selector: this.selectors.medias[0], context: this, handler: handleMediaClick });
      }

      setActiveMedia(variant) {
        if (!variant) return;

        this.filterSlidesForVariant(variant);

        if (this.slider) {
          try {
            if (this.slider.wrapperEl) {
              this.slider.wrapperEl.style.transitionDuration = "0ms";
              this.slider.wrapperEl.style.transform = "";
            }
            this.slider.update();
            this.slider.slideTo(0, 0, false);
            requestAnimationFrame(() => {
              if (this.slider && this.slider.wrapperEl) this.slider.wrapperEl.style.transitionDuration = "";
            });
          } catch (e) {}
        }
        if (this.navSlider) {
          try { this.navSlider.update(); this.navSlider.slideTo(0, 0, false); } catch (e) {}
        }

        switch (this.mediaMode) {
          case "slider":
            this.setActiveMediaForSlider(variant);
            break;
          case "featured-image":
            this.setActiveMediaForFeaturedImage(variant);
            break;
          default:
            this.handleGalleryMode(variant);
        }
      }

      setActiveMediaForSlider(variant) {
        if (variant.featured_media && this.slider && this.slider.wrapperEl) {
          const slidesLen = (this.slider.slides && this.slider.slides.length) || 0;
          if (!slidesLen) return;
          let target = 0;
          if (variant.featured_media.id) {
            const wantedId = String(variant.featured_media.id);
            for (let i = 0; i < slidesLen; i++) {
              const slide = this.slider.slides[i];
              if (slide.classList && slide.classList.contains("swiper-slide-duplicate")) continue;
              const mid = slide.dataset && slide.dataset.mediaId;
              if (mid && String(mid) === wantedId) { target = i; break; }
            }
          }
          this.slider.slideTo(Math.min(target, slidesLen - 1), 0, false);
        }
      }

      setActiveMediaForFeaturedImage(variant) {
        if (variant.featured_image) {
          const src = variant.featured_image.src;
          const { featuredImage } = this.domNodes;
          const img = featuredImage && featuredImage.querySelector("img");
          if (img && src) img.src = src;
        }
      }

      handleGalleryMode(variant) {
        if (this.view !== "featured-product" && variant && variant.featured_media) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) this.scrollIntoView(selectedMedia);
        }
      }

      scrollIntoView(selectedMedia) {
        selectedMedia.scrollIntoView({ behavior: "smooth" });
      }

      getProductJson() {
        return fetch(window.Shopify.routes.root + `products/${this.dataset.productHandle}.js`)
          .then((response) => {
            if (!response.ok) throw new Error("Network response was not ok");
            return response.json();
          })
          .then((product) => product)
          .catch((error) => {
            console.error("There has been a problem with your fetch operation:", error);
          });
      }
    }
  );
}
